import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { useNavigate } from "react-router-dom";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import {
  useGetRulesMutation,
  useUpdateRuleStatusMutation,
} from "../../../features/tenant/rules/rulesApi";
import { toast } from "react-hot-toast";
import {
  FiEye, FiEdit2, FiCopy, FiZap, FiRepeat, FiTarget,
  FiBarChart2, FiPlus, FiSearch, FiToggleLeft, FiToggleRight, FiTrash2,
} from "react-icons/fi";

// ── helpers ────────────────────────────────────────────────────────────────────

function fmtDate(raw) {
  if (!raw) return "—";
  const d = (raw || "").split("T")[0];
  const [y, m, day] = (d || "").split("-");
  return y && m && day ? `${day}/${m}/${y}` : "—";
}

const STATUS_STYLES = {
  active:   "bg-green-50 text-green-700 border-green-200",
  inactive: "bg-gray-100 text-gray-500 border-gray-200",
  draft:    "bg-amber-50 text-amber-700 border-amber-200",
  deleted:  "bg-red-50 text-red-600 border-red-200",
};
const STATUS_DOT = {
  active:   "bg-green-500",
  inactive: "bg-gray-400",
  draft:    "bg-amber-400",
  deleted:  "bg-red-400",
};

const REWARD_MODE_STYLES = {
  one_time:   { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: FiZap,    label: "One-time" },
  repeating:  { cls: "bg-blue-50 text-blue-700 border-blue-200",       icon: FiRepeat, label: "Repeating" },
  milestone:  { cls: "bg-purple-50 text-purple-700 border-purple-200", icon: FiTarget, label: "Milestone" },
};

const EVENT_TYPE_STYLES = {
  scan:          "bg-sky-50 text-sky-700 border-sky-200",
  scheme_scan:   "bg-teal-50 text-teal-700 border-teal-200",
  points_scheme: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// ── sub-components ─────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const raw = (status || "").toLowerCase();
  const s = raw === "archived" ? "deleted" : raw;
  const cls = STATUS_STYLES[s] || "bg-gray-100 text-gray-500 border-gray-200";
  const dot = STATUS_DOT[s] || "bg-gray-400";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold border capitalize ${cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
      {s || "—"}
    </span>
  );
}

function RewardModeChip({ mode }) {
  const cfg = REWARD_MODE_STYLES[mode] || { cls: "bg-gray-100 text-gray-500 border-gray-200", icon: FiZap, label: mode };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${cfg.cls}`}>
      <Icon className="h-3 w-3 shrink-0" />
      {cfg.label}
    </span>
  );
}

function EventChip({ eventType }) {
  const cls = EVENT_TYPE_STYLES[eventType] || "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${cls}`}>
      {eventType || "—"}
    </span>
  );
}

function ActionBtn({ onClick, title, icon: Icon, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border transition-colors ${className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {title}
    </button>
  );
}

// ── Status filter pills ────────────────────────────────────────────────────────

const STATUS_TABS = ["All", "active", "inactive", "draft", "deleted"];
// "deleted" is stored as "archived" on the backend
const STATUS_API = { deleted: "archived" };

function StatusTabs({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {STATUS_TABS.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className={`px-3 py-1.5 rounded text-xs font-medium border transition-colors capitalize ${
            value === s
              ? "bg-gray-900 text-white border-gray-900"
              : "bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:text-gray-800"
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

const RulesList = () => {
  const navigate = useNavigate();

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const [fetchRules, { data: rulesData, isLoading: rulesLoading }] = useGetRulesMutation();
  const [updateStatus] = useUpdateRuleStatusMutation();

  const auth = {
    token: sessionStorage.getItem("access_token"),
    tenant_id: JSON.parse(sessionStorage.getItem("vendorData") || "{}").tenant_id,
  };

  const handleStatusChange = async (id, newStatus) => {
    const msg = newStatus === "archived"
      ? "Delete this rule? It will stop applying and can no longer be edited or reactivated."
      : `Change status to "${newStatus}"?`;
    if (!window.confirm(msg)) return;
    try {
      await updateStatus({ id, status: newStatus, ...auth }).unwrap();
      toast.success(newStatus === "archived" ? "Rule deleted" : "Status updated");
      fetchRulesList();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to update status");
    }
  };

  const columns = [
    {
      name: "#",
      cell: (_, idx) => (
        <span className="text-xs text-gray-400 font-mono">{offset + idx + 1}</span>
      ),
      width: "52px",
    },
    {
      name: "Name",
      cell: (row) => (
        <div className="py-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{row.name}</p>
          {row.description && (
            <p className="text-[11px] text-gray-400 truncate max-w-[200px]">{row.description}</p>
          )}
        </div>
      ),
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Event",
      cell: (row) => <EventChip eventType={row.event_type} />,
      sortable: true,
      width: "140px",
    },
    {
      name: "Reward Mode",
      cell: (row) => <RewardModeChip mode={row.reward_mode} />,
      sortable: true,
      width: "130px",
    },
    {
      name: "Status",
      cell: (row) => <StatusChip status={row.status} />,
      sortable: true,
      width: "100px",
    },
    {
      name: "Valid Period",
      cell: (row) => (
        <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
          {fmtDate(row.valid_from)}
          <span className="mx-1 text-gray-300">→</span>
          {row.valid_until ? fmtDate(row.valid_until) : <span className="text-gray-400">ongoing</span>}
        </span>
      ),
      width: "190px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1 flex-wrap py-1">
          <ActionBtn
            onClick={() => navigate(`view/${row.id}`)}
            title="View"
            icon={FiEye}
            className="bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
          />
          {(row.status === "draft" || row.status === "inactive") && (
            <ActionBtn
              onClick={() => navigate(`edit/${row.id}`)}
              title="Edit"
              icon={FiEdit2}
              className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
            />
          )}
          <ActionBtn
            onClick={() => navigate("add", { state: { cloneFrom: row } })}
            title="Clone"
            icon={FiCopy}
            className="bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
          />
          <ActionBtn
            onClick={() => navigate(`analytics/${row.id}`)}
            title="Stats"
            icon={FiBarChart2}
            className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
          />
          {(row.status === "draft" || row.status === "inactive") && (
            <ActionBtn
              onClick={() => handleStatusChange(row.id, "active")}
              title="Activate"
              icon={FiToggleRight}
              className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
            />
          )}
          {row.status === "active" && (
            <ActionBtn
              onClick={() => handleStatusChange(row.id, "inactive")}
              title="Deactivate"
              icon={FiToggleLeft}
              className="bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100"
            />
          )}
          {row.status !== "archived" && (
            <ActionBtn
              onClick={() => handleStatusChange(row.id, "archived")}
              title="Delete"
              icon={FiTrash2}
              className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
            />
          )}
        </div>
      ),
      minWidth: "320px",
    },
  ];

  const fetchRulesList = () => {
    fetchRules({
      limit,
      offset,
      name: searchTerm || undefined,
      status: statusFilter !== "All" ? (STATUS_API[statusFilter] || statusFilter) : undefined,
      ...auth,
    });
  };

  useEffect(() => {
    const t = setTimeout(fetchRulesList, 400);
    return () => clearTimeout(t);
  }, [limit, offset, searchTerm, statusFilter]); // eslint-disable-line

  const handlePageChange = (page) => setOffset((page - 1) * limit);
  const handlePerRowsChange = (n) => setLimit(n);

  const primaryColor =
    JSON.parse(sessionStorage.getItem("vendorTheme") || "{}")?.theme?.color_shades || {};

  return (
    <div className="flex w-full bg-gray-100 min-h-screen">
      <div className="w-full">

        {/* ── Page Header ── */}
        <div className="bg-white border-b border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-gray-900">Rules Management</h1>
            <LinksComponent />
          </div>
          <button
            type="button"
            onClick={() => navigate("add")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-colors"
            style={{ backgroundColor: primaryColor["700"] || "#3b82f6" }}
          >
            <FiPlus className="h-4 w-4" />
            Add Rule
          </button>
        </div>

        <div className="p-4 space-y-3">

          {/* ── Filters ── */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
            <div className="relative w-full sm:w-72">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by rule name…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
              />
            </div>
            <StatusTabs value={statusFilter} onChange={setStatusFilter} />
          </div>

          {/* ── Table ── */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <DataTableComponent
              columns={columns}
              data={rulesData?.body || []}
              loading={rulesLoading}
              totalRows={rulesData?.total || 0}
              handlePerRowsChange={handlePerRowsChange}
              handlePageChange={handlePageChange}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default RulesList;
